import { isValidAutomergeUrl, } from "@automerge/automerge-repo/slim";
import { importModuleFromFolderDocUrl } from "./packages.js";
// todo this can be a function that takes a plugin system and returns a change
// handler
/**
 * This class watches a moduleSettingsDoc and loads modules based on the contents therein.
 * It also watches the modules themselves for changes and reloads them when they change.
 */
export class ModuleWatcher {
    repo;
    urls;
    handles;
    doneLoading;
    #watchedModules = new Set();
    onLoad;
    constructor(repo, urls, callback) {
        this.repo = repo;
        this.urls = Array.isArray(urls) ? urls : [urls];
        this.onLoad = callback;
        this.doneLoading = this.init();
    }
    onChange = () => this.load().catch(console.error);
    async init() {
        this.handles = (await Promise.allSettled(this.urls.map(async (url) => this.repo.find(url))))
            .filter((result) => {
            return result.status == "fulfilled";
        })
            .map((result) => result.value);
        for (const handle of this.handles) {
            handle.addListener("change", this.onChange);
        }
        await this.load();
    }
    async loadModules(modules) {
        await Promise.all(modules.map(async (importName) => {
            this.setDocWatcher(importName);
            await this.announce(importName).catch((error) => {
                console.log(new Error(`Failed to load module ${importName}: ${error}`, {
                    cause: error,
                }));
            });
        }));
    }
    async loadSuggestedImportUrl(docUrl) {
        const handle = await this.repo.find(docUrl);
        const doc = handle.doc();
        const url = doc["@patchwork"]?.suggestedImportUrl;
        return url && (await this.loadModules([url]));
    }
    async importModuleSafe(importName) {
        try {
            const valid = isValidAutomergeUrl(importName);
            const mod = valid
                ? importModuleFromFolderDocUrl(importName)
                : import(/* @vite-ignore */ importName);
            return mod;
        }
        catch (error) {
            console.error(`%c Failed to import ${importName}`, "color: #000, background: #ffbcef", error);
        }
    }
    async announce(importName) {
        const mod = await this.importModuleSafe(importName);
        mod && this.onLoad(importName, mod);
    }
    // TODO: This is a bit janky and relies on a bunch of heuristics.
    // It would be better to watch all the files in the folder recursively
    // and to have some relationship with those other than just parsing the URL.
    setDocWatcher(importName) {
        if (this.#watchedModules.has(importName))
            return;
        this.#watchedModules.add(importName);
        const docUrl = isValidAutomergeUrl(importName)
            ? importName
            : importName.match(/\/automerge\/(\w+)\//)?.[1];
        if (!docUrl)
            return;
        this.repo.find(docUrl).then((handle) => {
            let previousSyncAtTime = handle.doc().lastSyncAt || 0;
            handle.on("change", () => {
                const lastSyncAt = handle.doc().lastSyncAt || 0;
                if (lastSyncAt <= previousSyncAtTime) {
                    console.log("handle updated but not lastSyncAt");
                    return;
                }
                previousSyncAtTime = lastSyncAt;
                const versionedImport = handle.view(handle.heads()).url;
                console.log(`change in ${importName}, reloading at ${versionedImport}`);
                this.announce(versionedImport);
            });
        });
    }
    async load() {
        if (!this.handles)
            throw new Error("No handles");
        const promises = this.handles.map((handle) => {
            const doc = handle.doc();
            const { modules = [] } = doc;
            return this.loadModules(modules);
        });
        await Promise.all(promises);
    }
}
